import { useState, useCallback, useRef, useEffect } from 'react';
import { UserService } from '@/lib/api/user.service';
import type { UserContact } from '@/lib/api/user.service';
import type { GuestGroup } from '@/types/group.types';

// ── Types ─────────────────────────────────────────────────────

export type TriggerType = '/' | '@' | '#';

export interface SlashCommand {
  name: string;
  description: string;
  icon?: string;
}

export interface AutocompleteItem {
  id: string;
  label: string;
  description?: string;
  icon?: string;
  type: TriggerType;
  /** Original data object for further use */
  data?: SlashCommand | UserContact | GuestGroup;
}

export interface AutocompleteState {
  isOpen: boolean;
  trigger: TriggerType | null;
  query: string;
  items: AutocompleteItem[];
  selectedIndex: number;
  isLoading: boolean;
}

export interface UseChatAutocompleteOptions {
  /** Celebration ID for loading groups (optional) */
  celebrationId?: number;
  /** Extra slash commands to add beyond defaults */
  extraCommands?: SlashCommand[];
  /** Custom groups to show (pass externally loaded groups) */
  groups?: GuestGroup[];
}

// ── Default Slash Commands ────────────────────────────────────

const DEFAULT_COMMANDS: SlashCommand[] = [
  { name: 'help', description: 'Show available commands', icon: '❓' },
  { name: 'message', description: 'Start a direct message (/message @Name)', icon: '💬' },
  { name: 'create', description: 'Create a new celebration', icon: '🎉' },
  { name: 'templates', description: 'Browse celebration templates', icon: '📋' },
  { name: 'budget', description: 'Set or update budget', icon: '💰' },
  { name: 'invite', description: 'Invite guests to celebration', icon: '✉️' },
  { name: 'status', description: 'Check celebration status', icon: '📊' },
  { name: 'tasks', description: 'View or create tasks', icon: '✅' },
  { name: 'vendors', description: 'Search vendors & services', icon: '🏪' },
  { name: 'reset', description: 'Start over from scratch', icon: '🔄' },
];

// ── Hook ──────────────────────────────────────────────────────

export function useChatAutocomplete(options: UseChatAutocompleteOptions = {}) {
  const { extraCommands = [], groups = [] } = options;

  const [state, setState] = useState<AutocompleteState>({
    isOpen: false,
    trigger: null,
    query: '',
    items: [],
    selectedIndex: 0,
    isLoading: false,
  });

  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const allCommands = useRef<SlashCommand[]>([...DEFAULT_COMMANDS, ...extraCommands]);

  // Update commands when extraCommands change
  useEffect(() => {
    allCommands.current = [...DEFAULT_COMMANDS, ...extraCommands];
  }, [extraCommands]);

  // ── Trigger Detection ─────────────────────────────────────

  /**
   * Find the active trigger and query based on cursor position and input text.
   * Returns null if no trigger is active.
   */
  const detectTrigger = useCallback(
    (text: string, cursorPos: number): { trigger: TriggerType; query: string; startPos: number } | null => {
      if (cursorPos === 0 || !text) return null;

      // Walk backwards from cursor to find the trigger character
      const textBeforeCursor = text.slice(0, cursorPos);

      // Find the last trigger character that's either at start or preceded by whitespace
      for (let i = textBeforeCursor.length - 1; i >= 0; i--) {
        const char = textBeforeCursor[i];

        // Stop at whitespace — no trigger in this word
        if (/\s/.test(char)) return null;

        if (char === '/' || char === '@' || char === '#') {
          // Trigger must be at start of input or preceded by whitespace
          if (i === 0 || /\s/.test(textBeforeCursor[i - 1])) {
            const trigger = char as TriggerType;
            const query = textBeforeCursor.slice(i + 1);
            return { trigger, query, startPos: i };
          }
          return null;
        }
      }

      return null;
    },
    [],
  );

  // ── Item Builders ─────────────────────────────────────────

  const buildCommandItems = useCallback(
    (query: string): AutocompleteItem[] => {
      const filtered = allCommands.current.filter(
        (cmd) =>
          cmd.name.toLowerCase().includes(query.toLowerCase()) ||
          cmd.description.toLowerCase().includes(query.toLowerCase()),
      );
      return filtered.map((cmd) => ({
        id: `cmd-${cmd.name}`,
        label: `/${cmd.name}`,
        description: cmd.description,
        icon: cmd.icon,
        type: '/' as TriggerType,
        data: cmd,
      }));
    },
    [],
  );

  const buildGroupItems = useCallback(
    (query: string): AutocompleteItem[] => {
      const filtered = groups.filter((g) =>
        g.name.toLowerCase().includes(query.toLowerCase()),
      );
      return filtered.map((g) => ({
        id: `group-${g.id}`,
        label: `#${g.name}`,
        description: g.description || `${g.member_count ?? 0} members`,
        icon: g.icon || '👥',
        type: '#' as TriggerType,
        data: g,
      }));
    },
    [groups],
  );

  // ── User Search (debounced) ───────────────────────────────

  const searchUsers = useCallback(async (query: string) => {
    if (query.length < 3) return;

    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      const users = await UserService.searchUsers(query);
      const items: AutocompleteItem[] = users.map((u) => ({
        id: `user-${u.id}`,
        label: `@${u.full_name}`,
        description: u.email || '',
        icon: '👤',
        type: '@' as TriggerType,
        data: u,
      }));

      setState((prev) => ({
        ...prev,
        items,
        selectedIndex: 0,
        isLoading: false,
      }));
    } catch {
      setState((prev) => ({ ...prev, items: [], isLoading: false }));
    }
  }, []);

  // ── Main Handler ──────────────────────────────────────────

  /**
   * Call this on every input change or cursor movement.
   */
  const handleInputChange = useCallback(
    (text: string, cursorPos: number) => {
      // Clear any pending search
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }

      const result = detectTrigger(text, cursorPos);

      if (!result) {
        setState((prev) =>
          prev.isOpen
            ? { ...prev, isOpen: false, trigger: null, query: '', items: [], selectedIndex: 0, isLoading: false }
            : prev,
        );
        return;
      }

      const { trigger, query } = result;

      switch (trigger) {
        case '/': {
          const items = buildCommandItems(query);
          setState({
            isOpen: items.length > 0,
            trigger: '/',
            query,
            items,
            selectedIndex: 0,
            isLoading: false,
          });
          break;
        }
        case '#': {
          const items = buildGroupItems(query);
          setState({
            isOpen: items.length > 0,
            trigger: '#',
            query,
            items,
            selectedIndex: 0,
            isLoading: false,
          });
          break;
        }
        case '@': {
          // Only trigger user search after 3+ characters
          if (query.length < 3) {
            setState({
              isOpen: query.length > 0,
              trigger: '@',
              query,
              items: [],
              selectedIndex: 0,
              isLoading: query.length > 0,
            });
          } else {
            setState((prev) => ({
              ...prev,
              isOpen: true,
              trigger: '@',
              query,
              isLoading: true,
            }));
            // Debounce the API call
            searchTimeoutRef.current = setTimeout(() => {
              searchUsers(query);
            }, 300);
          }
          break;
        }
      }
    },
    [detectTrigger, buildCommandItems, buildGroupItems, searchUsers],
  );

  // ── Keyboard Navigation ───────────────────────────────────

  const moveSelection = useCallback((direction: 'up' | 'down') => {
    setState((prev) => {
      if (!prev.isOpen || prev.items.length === 0) return prev;

      let next = prev.selectedIndex + (direction === 'down' ? 1 : -1);
      if (next < 0) next = prev.items.length - 1;
      if (next >= prev.items.length) next = 0;

      return { ...prev, selectedIndex: next };
    });
  }, []);

  /**
   * Get the currently selected item (for Enter key).
   */
  const getSelectedItem = useCallback((): AutocompleteItem | null => {
    if (!state.isOpen || state.items.length === 0) return null;
    return state.items[state.selectedIndex] ?? null;
  }, [state.isOpen, state.items, state.selectedIndex]);

  /**
   * Apply a selection: replace the trigger+query in text with the chosen item.
   * Returns the new text and new cursor position.
   */
  const applySelection = useCallback(
    (text: string, cursorPos: number, item: AutocompleteItem): { newText: string; newCursorPos: number } => {
      const result = detectTrigger(text, cursorPos);
      if (!result) return { newText: text, newCursorPos: cursorPos };

      const { startPos } = result;
      const before = text.slice(0, startPos);
      const after = text.slice(cursorPos);
      const insertion = item.label + ' ';
      const newText = before + insertion + after;
      const newCursorPos = before.length + insertion.length;

      return { newText, newCursorPos };
    },
    [detectTrigger],
  );

  // ── Close ─────────────────────────────────────────────────

  const close = useCallback(() => {
    setState((prev) =>
      prev.isOpen
        ? { ...prev, isOpen: false, trigger: null, query: '', items: [], selectedIndex: 0, isLoading: false }
        : prev,
    );
  }, []);

  // ── Cleanup ───────────────────────────────────────────────

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  return {
    state,
    handleInputChange,
    moveSelection,
    getSelectedItem,
    applySelection,
    close,
  };
}
